import React, { useMemo, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { RefreshCw, Search } from 'lucide-react';

import { formatDate, getOrderCode, orderQuantityTotal, supplierLabel } from './selloutUtils';

export default function SellOutOrderSelector({
  supplier,
  orders,
  selectedOrderIds,
  onSelectedOrderIdsChange,
  loading,
  onRefresh,
}) {
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = (orders || []).map((order) => ({
      id: order.id,
      order,
      code: getOrderCode(order),
      date: formatDate(order.order_date || order.createdAt || order.dateTime),
      status: order.orderStatus || '',
      quantity_total: orderQuantityTotal(order),
      supplier_name: supplierLabel(order.supplier) || supplierLabel(supplier),
    }));
    if (!term) return list;
    return list.filter((row) =>
      [row.code, row.status, row.supplier_name, row.date].some((value) =>
        String(value || '').toLowerCase().includes(term)
      )
    );
  }, [orders, search, supplier]);

  const columns = [
    { field: 'code', headerName: 'Orden', minWidth: 150, flex: 0.9 },
    { field: 'date', headerName: 'Fecha', width: 120 },
    { field: 'status', headerName: 'Estado', width: 120 },
    {
      field: 'quantity_total',
      headerName: 'Cantidad total',
      width: 140,
      align: 'right',
      headerAlign: 'right',
    },
  ];

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
        <TextField
          fullWidth
          size="small"
          label="Buscar orden"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <Search size={17} style={{ marginRight: 8 }} /> }}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshCw size={17} />}
          onClick={onRefresh}
          disabled={loading || !supplier?.id}
          sx={{ minWidth: 130 }}
        >
          Actualizar
        </Button>
      </Stack>

      <Box sx={{ height: 390, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedOrderIds}
          onRowSelectionModelChange={(model) => onSelectedOrderIdsChange(Array.from(model))}
          pageSizeOptions={[5, 10, 25]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{
            borderRadius: 1,
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f7f8fa' },
          }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary">
        {selectedOrderIds.length} orden{selectedOrderIds.length === 1 ? '' : 'es'} seleccionada
        {selectedOrderIds.length === 1 ? '' : 's'}
      </Typography>
    </Stack>
  );
}
